'use memo';

import { Link, Outlet, createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import ActionButton from '@/components/Button/ActionButton';
import Icon from '@/components/Icon/Icon';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import { HeaderTabs } from '@/components/Navigation/HeaderTabs';
import { Text } from '@/components/Typography/Text';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { ArchiveOperationInstanceDialog } from '@/features/operation-instances/components/ArchiveOperationInstanceDialog';
import { OperationPackageInstanceTargetLink } from '@/features/operation-package-instance/-components/OperationPackageInstanceTargetLink';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { defaultNS, useTranslation } from '@/lib/i18n';

import {
  usePostOperationPackageInstanceArchive,
  useSuspenseQueryOperationPackageInstance,
} from './_profile/-queries';
import { TaskStatus } from './_profile/-types/operation-package';

export const Route = createFileRoute(
  '/_staff/attendance-core/_layout/operation-packages/$id/patterns/$patternId/instances/$instanceId/_profile',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation(defaultNS);

  const { instanceId } = Route.useParams();
  const { data: instanceData } =
    useSuspenseQueryOperationPackageInstance(instanceId);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const { toast } = useToast();

  useDocumentTitle(
    `${instanceData.name} - ${t('pages.attendanceCore.instance.attendanceCore')}`,
  );

  const { mutate: archiveInstance, isPending: isArchivePending } =
    usePostOperationPackageInstanceArchive(instanceId, {
      onSuccess: () => {
        toast({
          description: t(
            'features.operationPackageInstance.action.archiveSuccess',
          ),
        });
        setDropdownOpen(false);
        setIsArchiveDialogOpen(false);
      },
      onError: (message) => {
        toast({
          variant: 'destructive',
          description: message,
        });
      },
    });

  const canArchive = instanceData.status === TaskStatus.InProgress;

  const handleArchiveClick = useCallback(() => {
    setDropdownOpen(false);
    setIsArchiveDialogOpen(true);
  }, []);

  return (
    <FillScreen>
      <VStack className="bg-neutral-fill px-12 pt-4">
        <HStack className="items-start justify-between">
          <VStack className="gap-3">
            <Link to="/attendance-core/operation-packages">
              <Icon
                iconName="ChevronRight"
                text={t('pages.attendanceCore.instance.attendanceCore')}
                variant="small"
                right
              />
            </Link>
            <Text variant="h4">{instanceData.name}</Text>
          </VStack>

          <HStack className="items-center gap-2">
            <OperationPackageInstanceTargetLink target={instanceData.target} />
            <Separator orientation="vertical" />
            {/*
              TODO: 現時点ではすべてのタスクが完了した＝完了なので完了ボタンが不要なのでコメントアウト。今後任意入力のタスクなどが実装された場合は、完了ボタンを復活させる。
              @see https://dress-code-inc.slack.com/archives/C086TDGRAL8/p1746168672912939
            */}
            {/* <CompleteButton
              routeParam={{
                packageId,
                patternId,
                instanceId,
              }}
              useSuspenseQueryOperationPackageInstance={
                useSuspenseQueryOperationPackageInstance
              }
              usePostOperationPackageInstanceApprove={
                usePostOperationPackageInstanceApprove
              }
            /> */}
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <ActionButton variant="ghost">
                  <Icon iconName="MoreHorizontalIcon" />
                </ActionButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>
                  <ActionButton
                    variant="ghost"
                    icon="ArchiveIcon"
                    iconColor="error"
                    onClick={handleArchiveClick}
                    disabled={!canArchive}
                  >
                    {t('features.operationPackageInstance.action.archive')}
                  </ActionButton>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ArchiveOperationInstanceDialog
              isOpen={isArchiveDialogOpen}
              onClose={() => setIsArchiveDialogOpen(false)}
              onConfirm={() => archiveInstance()}
              isPending={isArchivePending}
            />
          </HStack>
        </HStack>
        <HeaderTabs
          tabs={[
            {
              to: '/attendance-core/operation-packages/$id/patterns/$patternId/instances/$instanceId',
              children: t('pages.attendanceCore.instance.tabs.detail'),
              activeOptions: { exact: true },
            },
            {
              to: '/attendance-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/data',
              children: t('pages.attendanceCore.instance.tabs.data'),
              activeOptions: { exact: true },
            },
            {
              to: '/',
              children: t(
                'pages.attendanceCore.instance.tabs.operationActivity',
              ),
              disabled: true,
            },
          ]}
        />
      </VStack>
      <Outlet />
    </FillScreen>
  );
}
